import { useMemo, type ReactElement } from 'react'
import { MultiSelect } from '@mantine/core'
import { useGroupedTeamOptions } from '@hooks'
import { usePermissionCheck, usePermissionMap, useProfile, useTeams, useTranslation } from '@front/model'
import { type WithProgramForm } from './type'

export const ProgramScope = ({ form }: WithProgramForm): ReactElement => {
  const { t } = useTranslation()
  const { company } = useProfile()
  const { data: teams } = useTeams({ showArchived: 'all' })
  const permissionIds = usePermissionMap({ permissionType: 'program:manage' })
  const availableTeams = useMemo(() => {
    const teamIds = new Set(permissionIds)
    return teams?.filter((team) => teamIds.has(team.teamId)) ?? []
  }, [permissionIds, teams])
  const groupedTeamOptions = useGroupedTeamOptions(availableTeams, company)
  const { data: canHaveEmptyTeams } = usePermissionCheck({
    permissionType: 'program:manage',
    targetId: company?.companyId,
  })

  const error = useMemo(() => {
    if (canHaveEmptyTeams) return undefined
    if (!form.values.teamIds?.length) return t('At least one team must be selected')
    return undefined
  }, [canHaveEmptyTeams, form.values.teamIds, t])

  return (
    <MultiSelect
      data={groupedTeamOptions}
      clearable={canHaveEmptyTeams}
      searchable
      placeholder={t('Select teams')}
      label={t('Scope')}
      description={t(
        'Limits every auto enrollment trigger below to users in the selected teams. If no teams are selected, all users in the company can be auto enrolled. Setting a scope disables the "Terros User Start Date" trigger, which fires before a user joins a team.'
      )}
      {...form.getInputProps('teamIds')}
      error={error}
    />
  )
}
